import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Eye } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

const dayLabel = (d, lang) => new Date(`${d}T12:00:00Z`).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { weekday: "short", timeZone: "UTC" });

// Admin-only 7-day profile views bar chart with weekly total and change vs the previous week.
export function ProfileViewsChart({ compact = false, userId }) {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(userId ? `/auth/admin/users/${userId}/profile-views` : "/auth/me/profile-views").then((r) => setData(r.data)).catch(() => {}); }, [userId]);
  if (!data) return null;
  const max = Math.max(1, ...data.days.map((d) => d.count));
  const up = data.change_pct !== null && data.change_pct >= 0;
  return (
    <div className={`bg-white rounded-2xl border border-slate-200/80 ${compact ? "p-4" : "p-6"}`} data-testid="profile-views-chart">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Eye size={13} className="text-[#F5551E]" /> {t("pv_title")}</p>
          <p className="font-display text-3xl font-extrabold text-[#12234A] mt-1" data-testid="profile-views-week-total">{data.total.toLocaleString()}</p>
        </div>
        <div className="text-right">
          {data.change_pct === null ? (
            <span className="text-xs text-slate-400" data-testid="profile-views-change">{data.total ? t("pv_new") : t("pv_none")}</span>
          ) : (
            <span data-testid="profile-views-change" className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${up ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
              {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {up ? "+" : ""}{data.change_pct}% {t("pv_vs")}
            </span>
          )}
          <p className="text-[11px] text-slate-400 mt-1">{data.all_time.toLocaleString()} {t("pv_all")}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-2 items-end h-32" data-testid="profile-views-bars">
        {data.days.map((d, i) => (
          <div key={d.date} className="flex flex-col items-center justify-end h-full gap-1" data-testid={`profile-views-day-${d.date}`}>
            <span className="text-[11px] font-semibold text-[#12234A]">{d.count}</span>
            <div className={`w-full rounded-t-md transition-all duration-700 ${i === 6 ? "bg-[#F5551E]" : "bg-[#12234A]/80"}`}
              style={{ height: `${Math.max(4, (d.count / max) * 88)}px` }} title={`${d.date}: ${d.count}`} />
            <span className="text-[10px] text-slate-400">{i === 6 ? t("pv_today") : dayLabel(d.date, lang)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
